import { DefaultError, Enabled, Query, QueryKey } from '@tanstack/react-query';

const isEnabled = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  query: Query<TQueryFnData, TError, TData, TQueryKey>,
  enabled?: Enabled<TQueryFnData, TError, TData, TQueryKey>
): boolean => {
  if (enabled === undefined) {
    return true;
  }
  if (typeof enabled === 'function') {
    return enabled(query);
  }
  return enabled;
};

export function makeIsEnabled(
  ...predicates: Array<boolean | undefined>
): boolean;
export function makeIsEnabled<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  ...predicates: Array<
    Enabled<TQueryFnData, TError, TData, TQueryKey> | undefined
  >
): (query: Query<TQueryFnData, TError, TData, TQueryKey>) => boolean;
export function makeIsEnabled(
  ...predicates: Array<Enabled<unknown, unknown, unknown, QueryKey> | undefined>
): boolean | ((query: Query<unknown, unknown, unknown, QueryKey>) => boolean) {
  const definedPredicates = predicates.filter(
    (predicate) => predicate !== undefined
  );
  if (definedPredicates.every((p) => typeof p === 'boolean')) {
    return definedPredicates.length === 0 || definedPredicates.every(Boolean);
  }
  return (query: Query<unknown, unknown, unknown, QueryKey>): boolean => {
    if (!definedPredicates.length) {
      return true;
    }
    return definedPredicates.every((predicate) =>
      isEnabled(query as never, predicate as never)
    );
  };
}

export const makeRefetchOnlyFailed = (
  ...errorRefetchPairs: Array<[unknown, () => void]>
): { error: unknown; refetch: () => void } => {
  const refetches = errorRefetchPairs.filter(([hasError]) => hasError);
  if (!refetches.length) {
    return {
      error: false,
      refetch: (): void => {},
    };
  }
  return {
    error: refetches[0][0]!,
    refetch: (): void => {
      refetches.forEach(([, refetch]) => refetch());
    },
  };
};
